/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/ban-types */
import React from 'react';
import { createContext, PropsWithChildren, useEffect, useState } from 'react';
import { NotificationApi } from '../../services/notification-service/notification-service';
import { useConfig } from '@revenuewell/logic-config';
import { useOidc } from '@revenuewell/logic-oidc';
import { KazooCall, KazooCallChannel, ScreenPopContext } from './types';
import { VoiceApi } from '../../services/api-client/voice-api';
import { usePhone } from '../use-phone';
import { ChannelMessage } from '../use-kazoo/kazoo-wss-types';
import { useKazoo } from '../use-kazoo';
import { normalizeUSPhoneNumber } from '../../utils/formatter';

function mapChannel(message: ChannelMessage): KazooCallChannel {
  return {
    startedAt: new Date().getTime(),
    callId: message.data.call_id,
    phoneNumber: normalizeUSPhoneNumber(message.data.caller_id_number),
    readyToShow: false,
    expired: false
  };
}

function isLocationChannel(message: ChannelMessage, primaryPhoneNumber: string) {
  return (
    (normalizeUSPhoneNumber(message.data.caller_destination_number) === primaryPhoneNumber ||
      normalizeUSPhoneNumber(message.data.callee_id_number) === primaryPhoneNumber) &&
    message.data.call_direction === 'outbound'
  );
}

export const screenPopContext = createContext<ScreenPopContext>(null!);

export function ScreenPopProvider({ children }: PropsWithChildren<{}>) {
  const { config } = useConfig();
  const {
    calls: { allCalls }
  } = usePhone();
  const { oidcService, getClaims } = useOidc();
  const { channelCreateMessage, channelDestroyMessage } = useKazoo();

  const [kazooCalls, _setKazooCalls] = useState<KazooCall[]>([]);
  const [primaryPhoneNumber, setPrimaryPhoneNumber] = useState<string>();
  const [screenPopEnabled, setScreenPopEnabled] = useState<boolean>(false);

  const notificationApi = new NotificationApi(config, oidcService);
  const voiceApi = new VoiceApi(config, oidcService);
  const kazooCallsRef = React.useRef(kazooCalls);

  // The inbound "root" leg's call_id, shared by every ringing device leg as bridge_id.
  const getRootCallId = (message: any): string | undefined =>
    message?.data?.custom_channel_vars?.bridge_id || message?.data?.other_leg_call_id;

  useEffect(() => {
    console.log('SCREEN POP - KAZOO CALLS: ', kazooCalls);
  }, [kazooCalls]);

  useEffect(() => {
    console.log('SCREEN POP - ENABLED: ', screenPopEnabled);
  }, [screenPopEnabled]);

  useEffect(() => {
    notificationApi.getScreenPopSettings().then(setScreenPopEnabled);
    const interval = setInterval(
      async () => notificationApi.getScreenPopSettings().then(setScreenPopEnabled),
      60 * 1000
    );
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    (async () => {
      const claims = await getClaims();
      const phoneNumbers = await voiceApi.getAllPhoneNumbers(claims.locationId);
      const primaryPhone = phoneNumbers.find((p) => p.isPrimaryOutbound);
      if (primaryPhone) setPrimaryPhoneNumber(normalizeUSPhoneNumber(primaryPhone.phoneNumber));
    })();
  }, []);

  useEffect(() => {
    kazooCallsRef.current.forEach((kazooCall) => {
      const ucCall = Object.values(allCalls).find(
        (c) =>
          normalizeUSPhoneNumber(c.remoteIdentity) === normalizeUSPhoneNumber(kazooCall.phoneNumber) &&
          c.direction === 'inbound'
      );

      const hasCallPopup = ucCall !== undefined;

      // If a call pop was showing for this call and its UC call is now gone,
      // the call has ended - remove the screen pop instead of surfacing it,
      // rather than waiting for the delayed kazoo 'channel.destroy' or the 2 min timeout.
      if (kazooCall.hasCallPopup && !hasCallPopup) {
        removeKazooCall(kazooCall.phoneNumber);
        return;
      }

      kazooCall.hasCallPopup = hasCallPopup;
      updateKazooCall(kazooCall);
    });
  }, [allCalls]);

  useEffect(() => {
    if (!primaryPhoneNumber || primaryPhoneNumber === '') return;

    console.log('SCREEN POP - CHANNEL CREATE: ', channelCreateMessage);

    if (channelCreateMessage && screenPopEnabled && isLocationChannel(channelCreateMessage, primaryPhoneNumber)) {
      const newChannel = mapChannel(channelCreateMessage);
      const bridgeId = getRootCallId(channelCreateMessage);
      const kazooCall = kazooCallsRef.current.find((c) => c.phoneNumber === newChannel.phoneNumber);

      if (kazooCall) {
        kazooCall.channels.push(newChannel);
        if (!kazooCall.bridgeId) kazooCall.bridgeId = bridgeId;
        updateKazooCall(kazooCall);
      } else {
        const newKazooCall = {
          phoneNumber: newChannel.phoneNumber,
          bridgeId,
          channels: [newChannel]
        } as KazooCall;
        addKazooCall(newKazooCall);
        delayScreenPopRender(newKazooCall);
        removeScreenPopIn1Min(newKazooCall);
      }
    }
  }, [channelCreateMessage, screenPopEnabled, primaryPhoneNumber]);

  useEffect(() => {
    if (!primaryPhoneNumber || primaryPhoneNumber === '') return;

    console.log('SCREEN POP - CHANNEL DESTROY: ', channelDestroyMessage);

    if (channelDestroyMessage && screenPopEnabled) {
      const destroyedCallId = channelDestroyMessage.data?.call_id;

      // Remove the pop when the call's inbound root leg is destroyed. That leg lives for
      // the whole call and ends only at the real hangup, while the ringing device legs
      // come and go (losing legs die at answer with LOSE_RACE). Keying removal to the
      // root leg keeps the pop up through answer and clears it exactly at hangup. Note
      // the root leg's destroy is call_direction 'inbound', so it does NOT pass
      // isLocationChannel - we intentionally match by bridge id instead of that filter.
      const kazooCall = destroyedCallId
        ? kazooCallsRef.current.find((c) => c.bridgeId === destroyedCallId)
        : undefined;

      if (kazooCall) removeKazooCall(kazooCall.phoneNumber);
    }
  }, [channelDestroyMessage, screenPopEnabled, primaryPhoneNumber]);

  // wait for call pop to appear to make a descision
  // if screen pop should be diplayed
  const delayScreenPopRender = (kazooCall: KazooCall) => {
    setTimeout(() => {
      kazooCall.isReady = true;
      updateKazooCall(kazooCall);
    }, 1500);
  };

  // kazoo 'channel.destroy' event may not be fired,
  // so hide screen pop in any case after 1 min.
  const removeScreenPopIn1Min = (kazooCall: KazooCall) => {
    setTimeout(() => removeKazooCall(kazooCall.phoneNumber), 60 * 1000);
  };

  const addKazooCall = (call: KazooCall) => {
    kazooCallsRef.current.push(call);
    _setKazooCalls(kazooCallsRef.current);
  };

  const updateKazooCall = (call: KazooCall) => {
    const updatedArray = kazooCallsRef.current.map((c) => {
      return c.phoneNumber === call.phoneNumber ? call : c;
    });
    kazooCallsRef.current = updatedArray;
    _setKazooCalls(kazooCallsRef.current);
  };

  const removeKazooCall = (phoneNumber: string) => {
    kazooCallsRef.current = kazooCallsRef.current.filter((c) => c.phoneNumber !== phoneNumber);
    _setKazooCalls(kazooCallsRef.current);
  };

  const ignoreCall = async (phoneNumber: string) => {
    const call = kazooCallsRef.current.find((c) => c.phoneNumber === normalizeUSPhoneNumber(phoneNumber));
    if (call) {
      call.isIgnored = true;
      updateKazooCall(call);
    }
  };
  const providerValue = React.useMemo(
    () => ({
      primaryPhoneNumber: primaryPhoneNumber || '',
      screenPopEnabled,
      kazooCalls,
      ignoreCall
    }),
    [primaryPhoneNumber, screenPopEnabled, kazooCalls]
  );

  return <screenPopContext.Provider value={providerValue}>{children}</screenPopContext.Provider>;
}
